import { useEffect } from 'react'
import { useAuth } from '@/components/auth/AuthProvider'
import {
  isGlobalHelpAppPath,
  resolveHelpAppPath,
} from '@/components/help/help-app-path'
import { Loading } from '@/components/ui/loading'
import { useOrganizationsQuery } from '@/hooks/queries/useOrganizationsQuery'
import { useOrganizationStore } from '@/store/organization.store'
import { needsProfileSetup } from '@/utils/auth/auth-sync'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { buildOpenTargetPath } from './open-in-organization-path'

export {
  buildOpenTargetPath,
  OPEN_ROUTE_FALLBACK_PATH,
} from './open-in-organization-path'

const INACTIVE_MEMBERSHIP_STATUSES = new Set(['SUSPENDED', 'INACTIVE'])

/**
 * Resolver for org-agnostic deep links published by the public help center.
 *
 * The public site at help.scrambl.com renders in-app links as
 * `https://app.scrambl.com/open/<path>` because it has no way of knowing which
 * organization the reader belongs to. This component runs inside the protected
 * layout (so an unauthenticated visitor is bounced to `/login?redirect=...`
 * and lands back here after signing in), then rewrites the link to a real
 * in-app URL:
 *
 * - Global, non-org paths (`/settings`, `/help/*`, `/whats-new`, …) are
 *   navigated to as-is.
 * - Everything else is prefixed with an organization slug resolved from, in
 *   order: the current organization in the store, the last-used organization
 *   id in localStorage (the store itself is not persisted, so this is what
 *   survives a cold load from an external link), or the user's only
 *   organization.
 * - With several organizations and no usable current one, the user picks at
 *   `/organizations`.
 *
 * Search and hash are preserved; the splat is always rendered as a relative
 * in-app path (see `buildOpenTargetPath`), never an absolute URL.
 */
export function OpenInOrganizationRedirect({ path }: { path?: string } = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const { user, isAuthenticated, isLoading } = useAuth()
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )

  // MainLayout pre-fetches this, so it is usually cached already.
  const { data: orgs = [], isLoading: isOrgsLoading } = useOrganizationsQuery({
    enabled: isAuthenticated,
  })

  const targetPath = buildOpenTargetPath(
    path ?? params['*'],
    location.search,
    location.hash,
  )

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return
    }

    if (user && needsProfileSetup(user)) {
      navigate('/user-setup', { replace: true })
      return
    }

    // Global paths carry no organization prefix, so they can resolve before
    // the organizations query settles.
    if (isGlobalHelpAppPath(targetPath)) {
      navigate(targetPath, { replace: true })
      return
    }

    if (isOrgsLoading) {
      return
    }

    if (orgs.length === 0) {
      navigate('/organization-setup', { replace: true })
      return
    }

    const findOrgById = (organizationId: string | null | undefined) =>
      organizationId ? orgs.find(org => org.id === organizationId) : undefined

    let lastUsedOrganizationId: string | null = null
    try {
      lastUsedOrganizationId = localStorage.getItem('currentOrganizationId')
    } catch {
      // Storage can be unavailable (private mode, blocked cookies) - ignore.
    }

    const targetOrg =
      findOrgById(currentOrganization?.id) ??
      findOrgById(lastUsedOrganizationId) ??
      (orgs.length === 1 ? orgs[0] : undefined)

    if (!targetOrg?.slug) {
      // Several organizations and nothing to disambiguate with - let the user
      // choose. (`/organizations` has no return-path convention today.)
      navigate('/organizations', { replace: true })
      return
    }

    const membershipStatus = targetOrg.userMembership?.status
    if (
      membershipStatus &&
      INACTIVE_MEMBERSHIP_STATUSES.has(membershipStatus)
    ) {
      navigate('/organizations?suspended=true', { replace: true })
      return
    }

    // Subscription state is intentionally not checked here - OrgRouteGuard on
    // the org route handles that after we land.
    navigate(resolveHelpAppPath(targetOrg.slug, targetPath), { replace: true })
  }, [
    navigate,
    targetPath,
    orgs,
    currentOrganization,
    isLoading,
    isOrgsLoading,
    isAuthenticated,
    user,
  ])

  return (
    <div className="flex h-full min-h-[60vh] w-full items-center justify-center">
      <Loading variant="spinner" size="lg" />
    </div>
  )
}
